import {Component, Injector, Input, OnInit} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor,
  NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validators,
} from "@angular/forms";
import {AbstractComponent} from "../abstract.component";
import {CodebookService} from "@services";
import {Country} from "@model";
import {required} from "@utility";
import {
  isPossiblePhoneNumber,
  parsePhoneNumber,
  validatePhoneNumberLength
} from "libphonenumber-js";
import {TranslocoService} from "@ngneat/transloco";

@Component({
    selector: 'app-tel-input',
    templateUrl: './tel-input.component.html',
    styleUrl: './tel-input.component.scss',
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: TelInputComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: TelInputComponent
        },],
    standalone: false
})
export class TelInputComponent extends AbstractComponent implements OnInit, ControlValueAccessor, Validators {

  @Input() label?: string;
  @Input() numberPlaceholder?: string;
  @Input() searchPlaceholder?: string;
  @Input() defaultCountry?: string = "hr";


  countries: Country[] = [];
  filteredCountries: Country[] = [];
  selectedCountry: Country | undefined;
  localNumber: string | undefined;
  searchText: string | undefined

  touched: boolean = false;
  disabled: boolean = false;
  invalid: boolean = false;
  errors: ValidationErrors | null = null;
  initialized: boolean = false;

  onChange = (phoneNumber: string | undefined) => {
  };

  onTouched: () => void = () => {
  };

  constructor(private codebookService: CodebookService, private inj: Injector, private translocoService: TranslocoService ) {
    super();
  }

  ngOnInit() {
    this.subscribeToCountriesChange();
  }

  ngAfterViewInit() {
    const outerControl: AbstractControl | null = this.inj.get(NgControl).control;
    if (outerControl) {
      outerControl.markAsTouched = () => {
        this.touched = true;
      };
      outerControl.markAsUntouched = () => {
        this.touched = false
      };
    }
  }


  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }


  getDefaultCountry() {
    return this.countries.filter(country => country.isoCode === this.defaultCountry?.toUpperCase())[0]
  }


  selectCountry(country: Country) {
    this.selectedCountry = country;
    this.setNumber();
    this.markAsTouched();
  }

  onSearch(value: string) {
    this.searchText = value;
    this.filteredCountries = this.countries.filter(country => {
      const translated =  country.id ? this.translocoService.translate('app.common.country.' + country.id) : "";
      return translated.toLowerCase().includes(value?.toLowerCase()) ||
          country.name?.toLowerCase().includes(value?.toLowerCase())
    })
  }

  onNumberInput(value: string) {
    // Strip anything that isn't a digit
    this.localNumber = this.sanitizeDigits(value);
    this.setNumber();
    this.markAsTouched()
  }


  dropdownClosed(open: boolean) {
    this.onSearch("")
  }

  private sanitizeDigits(value: string | undefined): string | undefined {
    if (!value) {
      return value;
    }
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly === '' ? undefined : digitsOnly;
  }

  private initFields(number: string | undefined) {
    if (number && isPossiblePhoneNumber(number)) {
      const phoneNumber = parsePhoneNumber(number);
      if (phoneNumber) {
        if ("nationalNumber" in phoneNumber && phoneNumber.nationalNumber !== this.localNumber) {
          this.localNumber = phoneNumber.nationalNumber;
        }
        if ("country" in phoneNumber && phoneNumber.country !== this.selectedCountry?.isoCode?.toUpperCase()) {
          let country = this.countries.filter(country => country.isoCode === phoneNumber.country)[0]
          if (country) {
            this.selectedCountry = country;
          } else {
            /**
             * If there is a mismatch between our and libphonenumber-js call codes
             */
            const callCode = "+".concat(phoneNumber.countryCallingCode)
            let country = this.countries.filter(country => country.callCode === callCode)[0]
            if (country) {
              this.selectedCountry = country
            }
          }
        }
      }
    }
    else if (!this.initialized){
      this.selectedCountry = undefined;
      this.localNumber = this.sanitizeDigits(number);
    }
  }

  private buildFullNumber(): string | undefined {
    return this.selectedCountry && this.localNumber
        ? this.selectedCountry.callCode + this.localNumber
        : undefined;
  }

  private setNumber() {
    this.onChange(this.buildFullNumber());
  }


  writeValue(phoneNumber: string | undefined) {
    this.initFields(phoneNumber);
    this.initialized = true;
    const reconstructed = this.buildFullNumber();
    if (reconstructed !== phoneNumber) {
      Promise.resolve().then(() => this.setNumber());
    }
  }


  private subscribeToCountriesChange() {
    this.subscriptions.push(this.codebookService.countries.value.subscribe(val => {
      this.countries = val;
      this.filteredCountries = val;
      if (!this.selectedCountry) {
        this.selectedCountry = this.getDefaultCountry();
      }
    }))
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};
    const phoneNumber = control.value;
    const isRequired = control.hasValidator(required)
    if (!phoneNumber) {
      if (isRequired) {
        errors['required'] = true
      }
    } else {
      try {
        if (validatePhoneNumberLength(phoneNumber)) {
          errors['invalidPhoneNumber'] = true
        }
      } catch (e) {
        errors['invalidPhoneNumber'] = true;
      }
    }
    if (Object.keys(errors).length > 0) {
      this.errors = errors
      this.invalid = true;
    } else {
      this.errors = null;
      this.invalid = false;
    }
    return this.errors;
  }

}